// Element helper for the v1 shell. Plain TypeScript and the DOM, like the rest
// of the codebase — the Pixi canvas owns the frame budget, so a framework is
// not an option here (PLAN-03 §3).
//
// Text always goes in through `textContent`, never `innerHTML`: chicken names,
// barn labels and anything else a player types land in these elements, and a
// name is not markup.

export type Child = Node | string | number | null | undefined | false;

export interface HOptions {
  class?: string;
  /** Plain text. Escaped by the DOM itself — this is the only text path. */
  text?: string;
  /**
   * Attributes. `null`/`undefined` skips; a boolean is an HTML boolean
   * attribute (`disabled`, `hidden`); an ARIA attribute is passed as the string
   * 'true' or 'false', which is what aria-pressed wants.
   */
  attrs?: Record<string, string | number | boolean | null | undefined>;
  /** Inline styles, for the rare computed value (a bar width, a spot). */
  style?: Record<string, string | number>;
  /** Event listeners, added at creation so there is no unhandled-click window. */
  on?: Record<string, EventListenerOrEventListenerObject>;
}

function applyChildren(node: HTMLElement, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

/**
 * `h('button', { class: 'cf-btn' }, 'Water')`.
 *
 * The options bag is second so the common case reads as a tag, its look and
 * its content, with no config object in the way.
 */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: HOptions = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.class) node.className = options.class;
  if (options.text !== undefined) node.textContent = options.text;
  if (options.attrs) {
    for (const [name, value] of Object.entries(options.attrs)) {
      if (value === null || value === undefined) continue;
      // A boolean means an HTML boolean attribute: `disabled: true` sets it,
      // `disabled: false` removes it. Writing the string "false" would keep the
      // attribute and silently disable the control. ARIA wants the opposite, so
      // those callers pass String(value) — 'true'/'false' as text.
      if (typeof value === 'boolean') {
        if (value) node.setAttribute(name, '');
        else node.removeAttribute(name);
        continue;
      }
      node.setAttribute(name, String(value));
    }
  }
  if (options.style) {
    for (const [name, value] of Object.entries(options.style)) {
      node.style.setProperty(name, String(value));
    }
  }
  if (options.on) {
    for (const [type, handler] of Object.entries(options.on)) {
      node.addEventListener(type, handler);
    }
  }
  applyChildren(node, children);
  return node;
}

export function div(options: HOptions = {}, ...children: Child[]): HTMLDivElement {
  return h('div', options, ...children);
}

export function button(options: HOptions = {}, ...children: Child[]): HTMLButtonElement {
  return h('button', { ...options, attrs: { type: 'button', ...options.attrs } }, ...children);
}

/** A real `<a>` that never navigates, for in-shell navigation. */
export function link(options: HOptions = {}, ...children: Child[]): HTMLAnchorElement {
  return h('a', { ...options, attrs: { href: '#', role: 'button', ...options.attrs } }, ...children);
}

export function clear(node: Element): void {
  node.replaceChildren();
}

/**
 * Repaints `host` only when `key` changes.
 *
 * The sim publishes a state change every tick and the clock moves with it, so a
 * screen that rebuilds its whole subtree on every notification replaces the
 * node a thumb is on its way to — the tap then lands on a detached element, and
 * focus and pressed state are lost every second. Keying the repaint on what is
 * actually drawn keeps the nodes stable and the work proportional.
 */
export function paint(host: HTMLElement, key: string, build: () => Node): void {
  if (host.dataset.paintKey === key) return;
  host.dataset.paintKey = key;
  host.replaceChildren(build());
}

export function frag(...children: Child[]): DocumentFragment {
  const f = document.createDocumentFragment();
  applyChildren(f as unknown as HTMLElement, children);
  return f;
}

export interface IconSpec {
  /** SVG child markup from the mockups: `<path …></path>`, `<circle …></circle>`. */
  shapes: string;
  size?: number;
  /** Stroke colour token name from theme.css. Defaults to the ink token. */
  tone?: 'ink' | 'clay' | 'warn' | 'danger' | 'sage' | 'shade' | 'muted' | 'white';
  /** Visible label; when set the icon is exposed to assistive tech. */
  label?: string;
  strokeWidth?: number;
}

const TONE_VAR: Record<NonNullable<IconSpec['tone']>, string> = {
  ink: 'var(--cf-ink)',
  clay: 'var(--cf-clay)',
  warn: 'var(--cf-warn)',
  danger: 'var(--cf-danger)',
  sage: 'var(--cf-sage-ink)',
  shade: 'var(--cf-shade)',
  muted: 'var(--cf-ink-2)',
  white: 'var(--cf-white)',
};

/**
 * Inline stroke SVG, never emoji (docs/ui/README.md — Icons). The shape markup
 * is a constant from this codebase, never player input, which is why it is the
 * one place `innerHTML` is safe.
 */
export function icon(spec: IconSpec): SVGElement {
  const size = spec.size ?? 24;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', TONE_VAR[spec.tone ?? 'ink']);
  svg.setAttribute('stroke-width', String(spec.strokeWidth ?? 2));
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  if (spec.label) {
    svg.setAttribute('role', 'img');
  } else {
    // Every caller has a visible text label or an aria-label on the control,
    // so "image, water drop, Water" is noise rather than information.
    svg.setAttribute('aria-hidden', 'true');
  }
  svg.innerHTML = spec.shapes;
  return svg;
}

/** An icon inside a flex row, the shape every icon in a labelled row uses. */
export function iconSpan(spec: IconSpec): HTMLSpanElement {
  return h('span', { class: 'cf-icon' }, icon(spec));
}

export function srOnly(text: string): HTMLSpanElement {
  return h('span', { class: 'cf-visually-hidden', text });
}
